import type { Board } from "../board";
import { DamageType } from "../damageType";
import type { Direction } from "../direction";
import { directionDelta } from "../direction";
import { Timing } from "../effects/effect";
import { Explosion } from "../effects/explosion";
import { Laser } from "../effects/laser";
import { isProjectileHittable } from "../interfaces";
import { PhysicalThingWithHp, STD_BASE_HP } from "../physicalThingWithHp";
import { BlackHoleProjectile } from "../projectiles/blackHoleProjectile";
import type { Player } from "../player";
import { Chasm } from "../things/chasm";
import { LavaPool } from "../things/lavaPool";
import { MultiTileThingPart } from "../things/multitile/multiTileThingPart";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { Weapon } from "../weapon";
import { WeaponVisual } from "../weaponVisual";
import type { XY } from "../xy";

const RANGE = 90;

export class LaserGun extends Weapon {
	static key = "LaserGun";
	constructor(owner: Player, ammo: number = 5) {
		super(owner, ammo);
	}

	override getWeaponVisual(): WeaponVisual {
		return WeaponVisual.HEAT_LAUNCHER;
	}

	fire(direction: Direction, board: Board): void {
		board.addNonPhysicalThing(new Sound(SoundId.LASER_BEAM));
		const delta = directionDelta(direction);
		let current = this.owner.xy.add(delta);

		for (let n = 0; n < RANGE; n++) {
			if (!board.isInBounds(current)) break;
			if (BlackHoleProjectile.blocksCell(board, current)) {
				// Swallowed: draw the final segment into the hole's rim, then stop
				const laser = new Laser(current, direction);
				laser.setTiming(Timing.BEFORE_STATE_UPDATE);
				board.addEffect(laser);
				break;
			}
			if (!this.isPassableByLaser(board, current)) {
				// Non-passable cell: hit multi-tile thing parts (robots) but stop
				this.hitMultiTilePartsAt(board, current, direction);
				break;
			}

			const laser = new Laser(current, direction);
			laser.setTiming(Timing.BEFORE_STATE_UPDATE);
			board.addEffect(laser);

			const things = board.getCell(current).getThings();
			const targets = things
				.filter((t) => t.active)
				.filter((t) => isProjectileHittable(t) && t.isHittable())
				.filter((t) => t instanceof PhysicalThingWithHp) as PhysicalThingWithHp[];

			for (const target of targets) {
				target.applyDamage(DamageType.EXPLOSION, STD_BASE_HP, null);
				board.addEffect(new Explosion(target.xy));
			}

			current = current.add(delta);
		}
	}

	private hitMultiTilePartsAt(board: Board, xy: XY, direction: Direction): void {
		const parts = board.getCell(xy).getThings()
			.filter((t) => t.active && t instanceof MultiTileThingPart && t.isHittable());
		if (parts.length === 0) return;

		const laser = new Laser(xy, direction);
		laser.setTiming(Timing.BEFORE_STATE_UPDATE);
		board.addEffect(laser);
		for (const part of parts) {
			(part as PhysicalThingWithHp).applyDamage(DamageType.EXPLOSION, STD_BASE_HP, null);
			board.addEffect(new Explosion(xy));
		}
	}

	private isPassableByLaser(board: Board, xy: XY): boolean {
		const things = board.getCell(xy).getThings();
		const blocked = things.some((t) => t.active && t.blocksMovement);
		if (!blocked) return true;
		return things.some((t) => t instanceof Chasm || t instanceof LavaPool);
	}

	static deserialize(data: any, owner: Player): LaserGun {
		const gun = new LaserGun(owner);
		gun.ammo = data.ammo;
		return gun;
	}
}
